import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { pb } from "../services/pocketbase";

const TournamentEditor = ({ open, onClose, tournament = null }) => {
  const [formData, setFormData] = useState({
    name: tournament?.name || "",
    start_date: tournament?.start_date
      ? new Date(tournament.start_date).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
    end_date: tournament?.end_date
      ? new Date(tournament.end_date).toISOString().slice(0, 16)
      : new Date(new Date().setDate(new Date().getDate() + 1))
          .toISOString()
          .slice(0, 16),
    description: tournament?.description || "",
    max_participants: tournament?.max_participants || 8,
    unlimited_participants: tournament?.max_participants === 0 ? true : false,
    status: tournament?.status || "upcoming",
    game: tournament?.game || "blackops6",
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    tournament?.thumbnail
      ? `${pb.baseUrl}/api/files/tournaments/${tournament.id}/${tournament.thumbnail}`
      : null
  );

  // Function to send webhook notification
  const sendWebhookNotification = async (eventData, isNew = false) => {
    try {
      const currentUser = pb.authStore.model;
      const timestamp = new Date().toISOString();

      let fields = [];
      let description = "";

      if (isNew) {
        // New event created
        description = `**New event created by ${currentUser.username}**`;
        fields = [
          {
            name: "Event Name",
            value: eventData.name,
            inline: true,
          },
          {
            name: "Game",
            value: getGameDisplayName(eventData.game),
            inline: true,
          },
          {
            name: "Start Date",
            value: new Date(eventData.start_date).toLocaleString(),
            inline: true,
          },
          {
            name: "End Date",
            value: new Date(eventData.end_date).toLocaleString(),
            inline: true,
          },
          {
            name: "Max Participants",
            value:
              eventData.max_participants === 0
                ? "Unlimited"
                : eventData.max_participants.toString(),
            inline: true,
          },
          {
            name: "Status",
            value:
              eventData.status.charAt(0).toUpperCase() +
              eventData.status.slice(1),
            inline: true,
          },
          {
            name: "Description",
            value: eventData.description || "No description provided",
          },
        ];
      } else {
        // Event edited - find changes
        description = `**Event edited by ${currentUser.username}**`;

        // Compare old and new values
        const changes = [];
        if (tournament.name !== eventData.name) {
          changes.push({
            name: "Name",
            value: `Changed from "${tournament.name}" to "${eventData.name}"`,
          });
        }

        if (tournament.game !== eventData.game) {
          changes.push({
            name: "Game",
            value: `Changed from "${getGameDisplayName(
              tournament.game
            )}" to "${getGameDisplayName(eventData.game)}"`,
          });
        }

        if (tournament.start_date !== eventData.start_date) {
          changes.push({
            name: "Start Date",
            value: `Changed from "${new Date(
              tournament.start_date
            ).toLocaleString()}" to "${new Date(
              eventData.start_date
            ).toLocaleString()}"`,
          });
        }

        if (tournament.end_date !== eventData.end_date) {
          changes.push({
            name: "End Date",
            value: `Changed from "${new Date(
              tournament.end_date
            ).toLocaleString()}" to "${new Date(
              eventData.end_date
            ).toLocaleString()}"`,
          });
        }

        if (tournament.max_participants !== eventData.max_participants) {
          const oldValue =
            tournament.max_participants === 0
              ? "Unlimited"
              : tournament.max_participants.toString();
          const newValue =
            eventData.max_participants === 0
              ? "Unlimited"
              : eventData.max_participants.toString();
          changes.push({
            name: "Max Participants",
            value: `Changed from "${oldValue}" to "${newValue}"`,
          });
        }

        if (tournament.status !== eventData.status) {
          changes.push({
            name: "Status",
            value: `Changed from "${
              tournament.status.charAt(0).toUpperCase() +
              tournament.status.slice(1)
            }" to "${
              eventData.status.charAt(0).toUpperCase() +
              eventData.status.slice(1)
            }"`,
          });
        }

        if (tournament.description !== eventData.description) {
          changes.push({
            name: "Description",
            value: "Description was updated",
          });
        }

        // Add event name as first field
        fields = [
          {
            name: "Event Name",
            value: eventData.name,
            inline: false,
          },
          ...changes.map((change) => ({
            name: change.name,
            value: change.value,
            inline: false,
          })),
        ];

        // If no changes detected
        if (changes.length === 0) {
          fields.push({
            name: "Note",
            value: "No significant changes detected",
          });
        }
      }

      // Prepare webhook data
      const webhookData = {
        content: isNew ? "New Event Created! 🎮" : "Event Updated! 📝",
        embeds: [
          {
            title: isNew ? "New Event Created" : "Event Updated",
            description: description,
            color: isNew ? 0x00ff00 : 0xffaa00, // Green for new, orange for updates
            fields: fields,
            timestamp: timestamp,
            // Add thumbnail if the event has an image
            image:
              eventData.id && eventData.thumbnail
                ? {
                    url: `${pb.baseUrl}/api/files/tournaments/${eventData.id}/${eventData.thumbnail}`,
                  }
                : null,
            footer: {
              text: `TOG Events | ${isNew ? "Created" : "Updated"} by ${
                currentUser.username
              }`,
            },
          },
        ],
      };

      // Remove null thumbnail if it doesn't exist
      if (!webhookData.embeds[0].image) {
        delete webhookData.embeds[0].image;
      }

      // Send to Discord webhook
      const response = await fetch("/api/webhooks/event-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        console.error("Failed to send webhook notification");
      }
    } catch (err) {
      console.error("Error sending webhook notification:", err);
    }
  };

  // Helper function to get readable game names
  const getGameDisplayName = (gameId) => {
    const games = {
      blackops6: "Black Ops 6",
      gta5: "GTA 5",
      other: "Other Event",
    };
    return games[gameId] || gameId;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        description: formData.description,
        max_participants: formData.unlimited_participants
          ? 0
          : parseInt(formData.max_participants, 10),
        status: formData.status,
        game: formData.game,
        organizer: pb.authStore.model.id,
      };

      if (tournament?.id) {
        // For updates
        if (thumbnail) {
          // If there's a new image, use FormData
          const formDataToSubmit = new FormData();
          Object.entries(data).forEach(([key, value]) => {
            formDataToSubmit.append(key, value);
          });
          formDataToSubmit.append("thumbnail", thumbnail);
          const updatedTournament = await pb
            .collection("tournaments")
            .update(tournament.id, formDataToSubmit, { $autoCancel: false });

          // Send webhook notification for edit
          await sendWebhookNotification(updatedTournament, false);
        } else {
          // If no new image, use regular JSON update
          const updatedTournament = await pb
            .collection("tournaments")
            .update(tournament.id, data, { $autoCancel: false });

          // Send webhook notification for edit
          await sendWebhookNotification(updatedTournament, false);
        }
      } else {
        // For new tournaments
        const formDataToSubmit = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          formDataToSubmit.append(key, value);
        });
        if (thumbnail) {
          formDataToSubmit.append("thumbnail", thumbnail);
        }
        const newTournament = await pb
          .collection("tournaments")
          .create(formDataToSubmit, { $autoCancel: false });

        // Send webhook notification for new tournament
        await sendWebhookNotification(newTournament, true);
      }
      onClose(true);
    } catch (err) {
      console.error("Error saving tournament:", err);
      alert(`Failed to save tournament: ${err.message}`);
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  useEffect(() => {
    setFormData({
      name: tournament?.name || "",
      start_date: tournament?.start_date
        ? new Date(tournament.start_date).toISOString().slice(0, 16)
        : new Date().toISOString().slice(0, 16),
      end_date: tournament?.end_date
        ? new Date(tournament.end_date).toISOString().slice(0, 16)
        : new Date(new Date().setDate(new Date().getDate() + 1))
            .toISOString()
            .slice(0, 16),
      description: tournament?.description || "",
      max_participants: tournament?.max_participants || 8,
      unlimited_participants: tournament?.max_participants === 0 ? true : false,
      status: tournament?.status || "upcoming",
      game: tournament?.game || "blackops6",
    });

    setPreviewUrl(
      tournament?.thumbnail
        ? `${pb.baseUrl}/api/files/tournaments/${tournament.id}/${tournament.thumbnail}`
        : null
    );
    setThumbnail(null);

    return () => {
      if (previewUrl && !previewUrl.includes(pb.baseUrl)) {
        URL.revokeObjectURL(previewUrl);
      }
    };
    // eslint-disable-next-line
  }, [tournament]);

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {tournament ? "Edit Event" : "Create Event"}
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Game selection field */}
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Game</InputLabel>
          <Select
            value={formData.game}
            label="Game"
            onChange={(e) => setFormData({ ...formData, game: e.target.value })}
            required
          >
            <MenuItem value="blackops6">Black Ops 6</MenuItem>
            <MenuItem value="gta5">GTA 5</MenuItem>
            <MenuItem value="other">Other Event</MenuItem>
          </Select>
        </FormControl>

        {/* Thumbnail upload section */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <input
            accept="image/*"
            type="file"
            onChange={handleThumbnailChange}
            style={{ display: "none" }}
            id="thumbnail-upload"
            required={!tournament?.thumbnail && !thumbnail}
          />
          <label htmlFor="thumbnail-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              sx={{ mb: 1 }}
            >
              {thumbnail ? "Change Thumbnail" : "Upload Thumbnail"}
            </Button>
          </label>

          {previewUrl && (
            <Box
              sx={{
                mt: 2,
                position: "relative",
                width: "100%",
                paddingTop: "56.25%", // 16:9 aspect ratio
                overflow: "hidden",
                borderRadius: "4px",
                backgroundColor: "rgb(40, 44, 52)", // Match the background color
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt="Tournament thumbnail"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "100%",
                  height: "auto",
                  minHeight: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            </Box>
          )}
        </Box>

        {/* Existing form fields */}
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        {/* Date fields in a row */}
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <TextField
            label="Start Date"
            type="datetime-local"
            value={formData.start_date}
            onChange={(e) =>
              setFormData({ ...formData, start_date: e.target.value })
            }
            sx={{ flex: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
          <TextField
            label="End Date"
            type="datetime-local"
            value={formData.end_date}
            onChange={(e) =>
              setFormData({ ...formData, end_date: e.target.value })
            }
            sx={{ flex: 1 }}
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
        </Box>

        <TextField
          fullWidth
          label="Description"
          multiline
          rows={4}
          margin="normal"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          required
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
            required
          >
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="ongoing">Ongoing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>

        {/* Add checkbox for unlimited participants */}
        <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.unlimited_participants}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unlimited_participants: e.target.checked,
                  })
                }
                name="unlimited_participants"
              />
            }
            label="Unlimited Participants"
          />
        </Box>

        {/* Show max participants field only if unlimited is not checked */}
        {!formData.unlimited_participants && (
          <TextField
            fullWidth
            label="Max Participants"
            type="number"
            margin="normal"
            value={formData.max_participants}
            onChange={(e) =>
              setFormData({ ...formData, max_participants: e.target.value })
            }
            required
            inputProps={{ min: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {tournament ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TournamentEditor;
